import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import type { CreatePracticePayload } from '../../../lib/adminDirectoryService';
import {
  createPracticeFormInitialValues,
  createPracticeFormSchema,
  getCreatePracticeValidationLines,
  type CreatePracticeFormValues,
} from '../../../schemas/organization.schema';
import { US_STATES } from '../../onboarding/constants';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

interface CreatePracticeModalProps {
  onClose: () => void;
  onCreatePractice: (payload: CreatePracticePayload) => Promise<void> | void;
}

export function CreatePracticeModal({
  onClose,
  onCreatePractice,
}: CreatePracticeModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationSummary, setValidationSummary] = useState<string[] | null>(null);

  const form = useForm<CreatePracticeFormValues>({
    initialValues: createPracticeFormInitialValues,
    validate: zod4Resolver(createPracticeFormSchema),
    validateInputOnBlur: true,
  });

  const stateInputProps = form.getInputProps('state');

  const handleSubmit = form.onSubmit(
    async (values) => {
      setValidationSummary(null);
      setSubmitting(true);
      setSubmitError(null);
      try {
        const addressLine = [values.addressLine1, values.addressLine2].filter(Boolean) as string[];
        const hasAddress = addressLine.length > 0 || values.city || values.state || values.postalCode;

        const payload: CreatePracticePayload = {
          practice: {
            name: values.name,
            ...(values.legalName ? { legalName: values.legalName } : {}),
            ...(values.contactEmail ? { contactEmail: values.contactEmail } : {}),
            ...(values.phone ? { phone: values.phone } : {}),
            ...(values.ein ? { ein: values.ein } : {}),
            ...(values.npi ? { npi: values.npi } : {}),
            ...(hasAddress
              ? {
                  address: {
                    ...(addressLine.length > 0 ? { line: addressLine } : {}),
                    ...(values.city ? { city: values.city } : {}),
                    ...(values.state ? { state: values.state } : {}),
                    ...(values.postalCode ? { postalCode: values.postalCode } : {}),
                  },
                }
              : {}),
          },
          owner: {
            firstName: values.ownerFirstName,
            lastName: values.ownerLastName,
            email: values.ownerEmail,
            ...(values.ownerPhone ? { phone: values.ownerPhone } : {}),
          },
        };

        await onCreatePractice(payload);
      } catch (err) {
        setSubmitError(getUserFacingErrorMessage(err, 'Failed to create practice'));
      } finally {
        setSubmitting(false);
      }
    },
    () => {
      setValidationSummary(getCreatePracticeValidationLines(form.getValues()));
    },
  );

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Create New Practice"
      size="lg"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {validationSummary && validationSummary.length > 0 && (
            <Alert color="orange" title="Fix these issues to create the practice">
              <Stack gap={6}>
                {validationSummary.map((line, i) => (
                  <Text key={`${i}-${line}`} size="sm">
                    {line}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Title order={5}>Practice Details</Title>

          <TextInput
            label="Practice Name"
            placeholder="e.g., Wellness Center Oakland"
            {...form.getInputProps('name')}
            required
          />

          <TextInput
            label="Legal Name"
            placeholder="Legal entity name (if different)"
            {...form.getInputProps('legalName')}
          />

          <SimpleGrid cols={2}>
            <TextInput
              label="Contact Email"
              type="email"
              placeholder="info@practice.com"
              {...form.getInputProps('contactEmail')}
            />
            <TextInput
              label="Phone"
              type="tel"
              placeholder="(555) 555-0100"
              autoComplete="tel"
              {...form.getInputProps('phone')}
            />
          </SimpleGrid>

          <SimpleGrid cols={2}>
            <TextInput
              label="EIN"
              placeholder="12-3456789"
              inputMode="numeric"
              maxLength={11}
              {...form.getInputProps('ein')}
            />
            <TextInput
              label="NPI"
              placeholder="1234567890"
              inputMode="numeric"
              maxLength={12}
              {...form.getInputProps('npi')}
            />
          </SimpleGrid>

          <TextInput
            label="Address Line 1"
            placeholder="Street address"
            {...form.getInputProps('addressLine1')}
          />
          <TextInput
            label="Address Line 2"
            placeholder="Suite, unit, etc."
            {...form.getInputProps('addressLine2')}
          />
          <SimpleGrid cols={3}>
            <TextInput
              label="City"
              placeholder="e.g., Oakland"
              {...form.getInputProps('city')}
            />
            <Select
              label="State"
              placeholder="Select state"
              data={US_STATES}
              searchable
              clearable
              {...stateInputProps}
              onChange={(v) => stateInputProps.onChange(v ?? '')}
            />
            <TextInput
              label="ZIP Code"
              placeholder="94612 or 94612-1234"
              autoComplete="postal-code"
              maxLength={10}
              {...form.getInputProps('postalCode')}
            />
          </SimpleGrid>

          <Divider />
          <Title order={5}>Practice Owner</Title>

          <SimpleGrid cols={2}>
            <TextInput
              label="First Name"
              placeholder="John"
              {...form.getInputProps('ownerFirstName')}
              required
            />
            <TextInput
              label="Last Name"
              placeholder="Smith"
              {...form.getInputProps('ownerLastName')}
              required
            />
          </SimpleGrid>

          <SimpleGrid cols={2}>
            <TextInput
              label="Email"
              type="email"
              placeholder="john.smith@practice.com"
              {...form.getInputProps('ownerEmail')}
              required
            />
            <TextInput
              label="Phone"
              type="tel"
              placeholder="(555) 555-0101"
              autoComplete="tel"
              {...form.getInputProps('ownerPhone')}
            />
          </SimpleGrid>

          <Alert color="blue" title="Practice Owner Account">
            <Text size="xs">
              Creating a practice will automatically create a Practice Owner account with the provided details. They will receive a branded setup email to set their password.
            </Text>
          </Alert>

          {submitError && (
            <Alert color="red" title="Error">
              <Text size="xs">{submitError}</Text>
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} type="button" disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              Create Practice
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
